/**
 * Client visitor context for API request headers and server log enrichment.
 * Persisted in sessionStorage so reloads keep lang/geo/origin when possible.
 */
import i18n from 'i18next';
import { SUPPORTED_LNGS } from './supportedLngs.js';

const STORAGE_KEY = 'gdetotam_visitor_v1';

const state = {
  lang: 'en',
  geo: null,
  originIata: null,
};

function load() {
  if (typeof sessionStorage === 'undefined') return;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed.lang) state.lang = parsed.lang;
    if (parsed.geo) state.geo = parsed.geo;
    if (parsed.originIata) state.originIata = parsed.originIata;
  } catch {
    /* ignore */
  }
}

function save() {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

load();

function normalizeLang(lng) {
  if (!lng) return null;
  const base = String(lng).split('-')[0].toLowerCase();
  return SUPPORTED_LNGS.includes(base) ? base : null;
}

/** Call once on app boot — language from URL path if present. */
export function initVisitorFromPath() {
  if (typeof window === 'undefined') return;
  const m = window.location.pathname.match(/^\/([a-z]{2})(?:\/|$)/);
  const fromPath = m ? normalizeLang(m[1]) : null;
  if (fromPath) {
    state.lang = fromPath;
    save();
  } else {
    const fromI18n = normalizeLang(i18n.language);
    if (fromI18n) state.lang = fromI18n;
  }
}

export function setVisitorLang(lng) {
  const safe = normalizeLang(lng);
  if (!safe) return;
  state.lang = safe;
  save();
}

/** geoip-lite shape from GET /api/geoip */
export function setVisitorGeo(geo) {
  if (!geo || typeof geo !== 'object') return;
  state.geo = {
    country: geo.country || '',
    region: geo.region || '',
    city: geo.city || '',
    lat: Array.isArray(geo.ll) ? geo.ll[0] : null,
    lon: Array.isArray(geo.ll) ? geo.ll[1] : null,
  };
  save();
}

export function setVisitorOriginIata(iata) {
  if (!iata) return;
  state.originIata = String(iata).toUpperCase();
  save();
}

export function getVisitorSnapshot() {
  return { ...state, geo: state.geo ? { ...state.geo } : null };
}

export function getVisitorHeaders() {
  const headers = {};
  const lang = normalizeLang(state.lang) || normalizeLang(i18n.language) || 'en';
  headers['X-GDetotam-Lang'] = lang;
  if (state.geo?.country) headers['X-GDetotam-Geo-Country'] = state.geo.country;
  if (state.geo?.region) headers['X-GDetotam-Geo-Region'] = String(state.geo.region);
  if (state.geo?.city) headers['X-GDetotam-Geo-City'] = state.geo.city;
  if (state.geo?.lat != null) headers['X-GDetotam-Geo-Lat'] = String(state.geo.lat);
  if (state.geo?.lon != null) headers['X-GDetotam-Geo-Lon'] = String(state.geo.lon);
  if (state.originIata) headers['X-GDetotam-Origin'] = state.originIata;
  return headers;
}
